import React, { useEffect, useState } from 'react';
import Placeholder from '../Placholder';
import { CommonModal, DateInput, LoadingBar, MultiSelect, PrimaryButton, Select } from '../../Common';
import Routes from '../../../routes';
import toast from 'react-hot-toast';
import ReportFilter from '../ReportFilter';
import moment from 'moment';
import { Box, Grid } from '@mui/material';
import '../../Common/Filters/Filters.css';
import UserInfo from '../../../UserInfo';
import RangeInput from '../../Common/RangeInput';
import { AllOption, CFC, fetchReportExport, getGWP, getLocationFiltersDropdown, getUpdatedSingleLocation, getValueOrDefault, HCFC, renderWeight, validateDate, validateRange } from '../utils';

const EpaSection608 = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [loadingExport, setLoadingExport] = useState(false);

    const [reportModal, setReportModal] = useState(false);
    const [maxGWP, setMaxGWP] = useState();
    const [maxChargeCapacity, setMaxChargeCapacity] = useState();
    const [ErrorObject, setErrorObject] = useState({});

    const [formData, setFormData] = useState({
        startDate: `01/01/${moment().year()}`,
        endDate: `${moment().format('MM/DD/YYYY')}`,
        regions: {},
        locations: {},
        states: {},
        refrigerantType: {},
        refrigerantGwpMin: 0,
        refrigerantGwpMax: "",
        chargeCapacityMin: 0,
        chargeCapacityMax: ""
    });

    const setDefaultFormData = (data) => {
        const maxGwp = getGWP([data.refrigerantType[HCFC], data.refrigerantType[CFC]], data.refrigerantGWP);
        const maxChargeCapacity = renderWeight(data.maxCapacity);
        setFormData(prev => ({
            ...prev,
            startDate: `01/01/${moment().year()}`,
            endDate: `${moment().format('MM/DD/YYYY')}`,
            regions: data.regions[AllOption],
            states: data.states[AllOption],
            refrigerantType: data.refrigerantType[HCFC],
            refrigerantGwpMin: 0,
            refrigerantGwpMax: maxGwp,
            chargeCapacityMin: renderWeight(50),
            chargeCapacityMax: maxChargeCapacity,
        }));
        setData({
            ...data,
            filterLocations: data.locations,
            filterStates: data.states,
            filterRegions: data.regions,
        });
        setMaxGWP(maxGwp);
        setMaxChargeCapacity(maxChargeCapacity);
    };

    const fetchDropdown = async () => {
        try {
            const response = await fetch(Routes.PowerBI.EpaDropdown());
            const data = await response.json();
            setDefaultFormData(data);
            setLoading(false);
        } catch (error) {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDropdown();
    }, []);

    const onHandleLocationRegionChange = (value, type) => {
        const { regionFilter, stateFilter, locationFilter, newChargeCapacityMax } = getLocationFiltersDropdown(value, type, data, formData, maxChargeCapacity);
        setData(prev => ({
            ...prev,
            filterRegions: regionFilter,
            filterStates: stateFilter,
            filterLocations: locationFilter,
        }));

        const { locations } = getUpdatedSingleLocation(value, type, formData, [data.locations]);
        const newChargeCapacityMin = newChargeCapacityMax < 50 ? 0 : renderWeight(50);
        setFormData(prev => ({
            ...prev,
            locations: locations[0],
            chargeCapacityMin: newChargeCapacityMin,
            chargeCapacityMax: newChargeCapacityMax,
        }));
        setMaxChargeCapacity(newChargeCapacityMax);
    };

    const validateFilter = (filter) => {
        const errorObject = {};
        const isEmpty = (value) =>
            value === null || value === undefined || value === "" || value.length === 0;

        const validLocations = new Set(data.filterLocations.map(item => item.value));
        const validStates = new Set(data.filterStates.map(item => item.value));

        filter.forEach(({ Property, Value }) => {
            if (isEmpty(Value)) {
                switch (Property) {
                    case "startDate":
                        errorObject.startDate = "Start Date";
                        break;
                    case "endDate":
                        errorObject.endDate = "End Date";
                        break;
                    case "refrigerantType":
                        errorObject.refrigerantType = "Refrigerant Type";
                        break;
                    case "locations":
                        errorObject.locations = "Locations";
                        break;
                    case "LocationsName":
                        errorObject.locationsName = "Location Name";
                        break;
                }
            } else {
                switch (Property) {
                    case "locations":
                        if (!validLocations.has(parseInt(Value))) {
                            errorObject.locations = "Location Name";
                        }
                        break;
                    case "states":
                        if (!Value.split(',').every(state => validStates.has(state))) {
                            errorObject.states = "Location State";
                        }
                        break;
                    case "date":
                        if (!validateDate(filter)) {
                            errorObject.date = "Start Date-End Date";
                        }
                        break;
                    case "refrigerantTypeGWP":
                        if (!validateRange(Value, maxGWP)) {
                            errorObject.refrigerantTypeGWP = "Refrigerant Type GWP";
                        }
                        break;
                    case "assetCapacity":
                        if (!validateRange(Value, maxChargeCapacity)) {
                            errorObject.assetCapacity = "Asset Charge Capacity";
                        }
                        break;
                    default:
                        break;
                }
            }
        });

        const hasError = Object.keys(errorObject).length > 0;
        if (hasError) {
            const errorMessages = Object.keys(errorObject)
                .map((key) => errorObject[key])
                .join(" and ");
            toast.error(
                `All required filters must be applied to generate the report. Please select the filter(s) ${errorMessages} and try again.`,
                { duration: 4000 }
            );
        }
        setErrorObject(errorObject);
        return hasError;
    };

    const exportReport = async () => {
        const startDate = formData.startDate ? moment(formData.startDate).format('MM/DD/YYYY') : "";
        const endDate = formData.endDate ? moment(formData.endDate).format('MM/DD/YYYY') : "";


        const filter = [
            {
                Property: "locations",
                Value: [formData.locations].map(location => location.value).toString()
            },
            {
                Property: "locationsName",
                Value: [formData.locations].map(location => location.name).toString()
            },
            {
                Property: "refrigerantTypeGWP",
                Value: `${formData.refrigerantGwpMin}-${formData.refrigerantGwpMax}`
            },
            {
                Property: "assetCapacity",
                Value: `${formData.chargeCapacityMin}-${formData.chargeCapacityMax}`
            },
            startDate && endDate && {
                Property: "date",
                Value: `${startDate}-${endDate}`
            },
            {
                Property: "regions",
                Value: [formData.regions].map(re => re.value).toString()
            },
            {
                Property: "refrigerantType",
                Value: [formData.refrigerantType].map(re => re.name).toString()
            },
            {
                Property: "refrigerantTypeId",
                Value: [formData.refrigerantType].map(re => re.value).toString()
            },
            {
                Property: "startDate",
                Value: startDate
            },
            {
                Property: "endDate",
                Value: endDate
            },
            {
                Property: "states",
                Value: [formData.states].map(sta => sta.value).toString()
            },
        ].filter(Boolean);

        if (validateFilter(filter)) {
            return;
        }

        setReportModal(false);
        setLoadingExport(true);
        await fetchReportExport({
            url: Routes.PowerBI.EpaSection608Report(),
            body: {
                Id: 20,
                Filters: filter,
            }
        });

        setDefaultFormData(data);
        setLoadingExport(false);
    };

    if (loading) {
        return <LoadingBar />;
    }

    return (
        <>
            <Placeholder
                message={'Customize and export your report by clicking the button below.'}
                subMessage={''}
            >
                <Grid item xs={12} md={3} sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: '2rem'
                }}>
                    <PrimaryButton
                        buttonText="GENERATE REPORT (.zip)"
                        onClick={() => {
                            setReportModal(true);
                        }}
                    />
                </Grid>
            </Placeholder>

            <CommonModal
                open={reportModal}
                close={() => setReportModal(false)}
                header="Generate EPA Section 608 Report"
            >
                <Grid container spacing={3} align='center'>
                    <Grid item xs={12} lg={12} align='center'>
                        <DateInput
                            allowPast
                            variant={"standard"}
                            date={formData.startDate}
                            onChange={new_date => setFormData(prev => ({
                                ...prev,
                                startDate: new_date
                            }))}
                            label="Start Date *"
                            width={65}
                            error={ErrorObject.startDate}
                        />
                    </Grid>
                    <Grid item xs={12} lg={12} align='center'>
                        <DateInput
                            allowPast
                            variant={"standard"}
                            date={formData.endDate}
                            onChange={new_date => setFormData(prev => ({
                                ...prev,
                                endDate: new_date
                            }))}
                            label="End Date *"
                            width={65}
                            error={ErrorObject.endDate}
                        />
                    </Grid>

                    <Grid item xs={8} lg={8} sx={{ marginLeft: 12 }} align='center'>
                        <Select
                            label='Region/Division *'
                            isFilterObject
                            variant={"standard"}
                            options={data.filterRegions}
                            value={formData.regions}
                            onChange={e => {
                                const newValue = getValueOrDefault(e, data.regions, "regions", formData);
                                console.log('newValue', newValue);
                                setFormData(prev => ({
                                    ...prev,
                                    regions: newValue
                                }));
                                onHandleLocationRegionChange([newValue], 'regions');
                            }}
                        >
                        </Select>
                    </Grid>

                    <Grid item xs={8} lg={8} sx={{ marginLeft: 12 }} align='center'>
                        <Select
                            label='Location Name *'
                            isFilterObject
                            variant={"standard"}
                            options={data.filterLocations}
                            value={formData.locations}
                            onChange={e => {
                                const newValue = getValueOrDefault(e, data.locations, "locations", formData);
                                console.log('newValue', newValue);
                                setFormData(prev => ({
                                    ...prev,
                                    locations: newValue
                                }));
                                onHandleLocationRegionChange([newValue], 'locations');
                            }}
                        >
                        </Select>
                    </Grid>
                    <Grid item xs={8} lg={8} sx={{ marginLeft: 12 }} align='center'>
                        <Select
                            label='Location State'
                            isFilterObject
                            variant={"standard"}
                            options={data.filterStates}
                            value={formData.states}
                            onChange={e => {
                                const newValue = getValueOrDefault(e, data.states, "states", formData);
                                setFormData(prev => ({
                                    ...prev,
                                    states: newValue
                                }));
                            }}
                        >
                        </Select>
                    </Grid>
                    <Grid item xs={8} lg={8} sx={{ marginLeft: 12 }} align='center'>
                        <Select
                            label='Refrigerant Type'
                            isFilterObject
                            variant={"standard"}
                            options={data.refrigerantType}
                            value={formData.refrigerantType}
                            onChange={e => {
                                const newValue = getValueOrDefault(e, data.refrigerantType, "refrigerantType", formData);
                                const gwp = getGWP([newValue], data.refrigerantGWP);
                                setMaxGWP(gwp);
                                setFormData(prev => ({
                                    ...prev,
                                    refrigerantType: newValue,
                                    refrigerantGwpMax: gwp
                                }));
                            }}
                        >
                        </Select>
                    </Grid>
                    <Grid item xs={8} lg={8} sx={{ marginLeft: 12 }} align='center'>
                        <RangeInput
                            label='REFRIGERANT TYPE GWP VALUE'
                            startRange={formData.refrigerantGwpMin}
                            endRange={formData.refrigerantGwpMax}
                            onClose={(start, end) => setFormData(prev => ({
                                ...prev,
                                refrigerantGwpMin: start,
                                refrigerantGwpMax: end,
                            }))}
                        />
                    </Grid>
                    <Grid item xs={8} lg={8} sx={{ marginLeft: 12 }} align='center'>
                        <RangeInput
                            label={`ASSET CHARGE CAPACITY(${UserInfo.weightUnits}) *`}
                            startRange={formData.chargeCapacityMin}
                            endRange={formData.chargeCapacityMax}
                            onClose={(start, end) => setFormData(prev => ({
                                ...prev,
                                chargeCapacityMin: start,
                                chargeCapacityMax: end,
                            }))}
                        />
                    </Grid>

                    <Grid item xs={12} lg={12} align='center'>
                        <Box mb={2} sx={{ width: '65%' }}>
                            <PrimaryButton
                                fullWidth
                                size='large'
                                variant='contained'
                                onClick={exportReport}
                                buttonText={"GENERATE REPORT (.zip)"}
                            >
                            </PrimaryButton>
                        </Box>
                    </Grid>
                </Grid>
            </CommonModal>
        </>
    );
};

export default EpaSection608;
